// src/components/SmartFlow/WorkflowBuilder.jsx
import React, { useState, useCallback, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  useReactFlow,
  ReactFlowProvider,
} from "reactflow";
import "reactflow/dist/style.css";
import dagre from "dagre";
import { api } from "../../services/api";

import { useWorkflowsStore } from "../../services/useWorkflowsStore";
import NodePalette from "./NodePalette";
import NodeConfigModal from "./nodeConfig/NodeConfigModal";
import WorkflowTemplates from "./WorkflowTemplates";
import ExecutionViewer from "./ExecutionViewer";

import { validateWorkflow } from "./workflowValidator";

// Import node registry
import { nodeRegistry, initializeNodes } from "./nodes";

// Initialize nodes (register all components)
initializeNodes();

// -----------------------------------------------------------------------------
// Componente interno que de fato constrói o fluxo
// -----------------------------------------------------------------------------
const WorkflowBuilderInner = () => {
  const { id: workflowId } = useParams();
  const navigate = useNavigate();
  const { fitView, project } = useReactFlow();

  const {
    currentWorkflow,
    loading,
    fetchWorkflowById,
    createWorkflow,
    updateWorkflow,
    executeWorkflow,
  } = useWorkflowsStore();

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const [workflowName, setWorkflowName] = useState("");
  const [workflowDescription, setWorkflowDescription] = useState("");
  const [triggerType, setTriggerType] = useState("MANUAL");

  const [selectedNode, setSelectedNode] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showExecutionViewer, setShowExecutionViewer] = useState(false);
  const [lastExecution, setLastExecution] = useState(null);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  // Estado para armazenar dados de input/output de cada node (para modal n8n-style)
  const [nodeExecutionData, setNodeExecutionData] = useState({});

  // Estado para nodeTypes do registry
  const [nodeTypes, setNodeTypes] = useState({});

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const createNodeId = (type) => `${type}-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

  const createFlowNode = (base) => {
    const id = base.id || createNodeId(base.node_type || base.type);

    return {
      id,
      type: base.node_type || base.type,
      position: {
        x: base.position_x ?? base.position?.x ?? 0,
        y: base.position_y ?? base.position?.y ?? 0,
      },
      data: {
        label: (base.label || base.node_type || base.type || "").replace(/_/g, " "),
        config: base.config || {},
        onEdit: () => handleNodeEdit(id),
        onDelete: () => handleNodeDelete(id),
      },
    };
  };

  const createFlowEdge = (base) => ({
    id: base.id || `edge-${base.source_node_id || base.source}-${base.target_node_id || base.target}`,
    source: base.source_node_id || base.source,
    target: base.target_node_id || base.target,
    label: base.condition || base.label || "",
    sourceHandle: base.condition || undefined,
    type: (base.condition || base.label) ? "smoothstep" : "default",
  });

  // Helper function to get input data from previous node(s)
  const getInputDataForNode = (nodeId) => {
    const incomingEdges = edges.filter((edge) => edge.target === nodeId);

    if (incomingEdges.length === 0) {
      return null;
    }

    if (incomingEdges.length === 1) {
      const sourceNodeId = incomingEdges[0].source;
      const sourceNodeOutput = nodeExecutionData[sourceNodeId]?.output;

      if (sourceNodeOutput) {
        return {
          $json: sourceNodeOutput.data,
          $metadata: {

            timestamp: sourceNodeOutput.timestamp,
          },
        };
      }
      return null;
    }

    const mergedData = {};
    incomingEdges.forEach((edge, index) => {
      const sourceNodeId = edge.source;
      const sourceNodeOutput = nodeExecutionData[sourceNodeId]?.output;

      if (sourceNodeOutput) {
        mergedData[`input_${index}`] = {
          $json: sourceNodeOutput.data,
          $metadata: {
            timestamp: sourceNodeOutput.timestamp,
          },
        };
      }
    });

    return Object.keys(mergedData).length > 0 ? mergedData : null;
  };

  // ---------------------------------------------------------------------------
  // Initialize node registry
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const initRegistry = async () => {
      try {
        // Initialize registry with backend nodes
        await nodeRegistry.initialize();

        // Get nodeTypes for ReactFlow
        const types = nodeRegistry.getReactFlowNodeTypes();
        console.log('[WorkflowBuilder] NodeTypes from registry:', types);
        console.log('[WorkflowBuilder] Registered components:', Array.from(nodeRegistry.components.keys()));

        setNodeTypes(types);

        console.log('[WorkflowBuilder] Node registry initialized with', Object.keys(types).length, 'types');
      } catch (error) {
        console.error('[WorkflowBuilder] Failed to initialize node registry:', error);
        // Fallback: use registered components even if API fails
        const fallbackTypes = nodeRegistry.getReactFlowNodeTypes();
        console.log('[WorkflowBuilder] Using fallback types:', fallbackTypes);
        setNodeTypes(fallbackTypes);
      }
    };

    initRegistry();
  }, []);

  // ---------------------------------------------------------------------------
  // Carregar workflow se estiver editando
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!workflowId) return;

    (async () => {
      try {
        const workflow = await fetchWorkflowById(workflowId);

        setWorkflowName(workflow.name || "");
        setWorkflowDescription(workflow.description || "");
        setTriggerType(workflow.trigger_type || "MANUAL");

        const flowNodes = (workflow.nodes || []).map((node) => createFlowNode(node));
        const flowEdges = (workflow.edges || []).map((edge) => createFlowEdge(edge));

        setNodes(flowNodes);
        setEdges(flowEdges);

        // Foca o diagrama após carregar
        setTimeout(() => {
          if (flowNodes.length > 0) {
            fitView({ padding: 0.2 });
          }
        }, 50);
      } catch (err) {
        console.error("Failed to load workflow:", err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId]);

  // ---------------------------------------------------------------------------
  // Eventos do React Flow
  // ---------------------------------------------------------------------------

  const onConnect = useCallback(
    (params) =>
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            type: "smoothstep",
          },
          eds
        )
      ),
    [setEdges]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const type = event.dataTransfer.getData("application/reactflow");
      if (!type) return;

      // Calcula posição relativa ao container do ReactFlow
      const bounds = event.currentTarget.getBoundingClientRect();
      const position = project({
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
      });

      const id = createNodeId(type);

      const newNode = {
        id,
        type,
        position,
        data: {
          label: type.replace(/_/g, " "),
          config: {},
          onEdit: () => handleNodeEdit(id),
          onDelete: () => handleNodeDelete(id),
        },
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [project, setNodes]
  );

  // Handle para duplo clique no node - abre configurações
  const onNodeDoubleClick = useCallback((event, node) => {
    handleNodeEdit(node.id);
  }, [nodes]);

  // Handle para tecla Delete - deleta nodes selecionados
  const onNodesDelete = useCallback((nodesToDelete) => {
    const nodeIds = nodesToDelete.map(n => n.id);
    setNodes((nds) => nds.filter((n) => !nodeIds.includes(n.id)));
    setEdges((eds) => eds.filter((e) => !nodeIds.includes(e.source) && !nodeIds.includes(e.target)));
  }, [setNodes, setEdges]);

  // ---------------------------------------------------------------------------
  // Edição de nós
  // ---------------------------------------------------------------------------

  const handleNodeEdit = (nodeId) => {
    const node = nodes.find((n) => n.id === nodeId);
    if (node) {
      setSelectedNode(node);
      setShowConfigModal(true);
    }
  };

  const handleNodeDelete = (nodeId) => {
    setNodes((nds) => nds.filter((n) => n.id !== nodeId));
    setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
  };

  const handleConfigSave = (config) => {
    if (selectedNode) {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === selectedNode.id
            ? {
                ...node,
                data: {
                  ...node.data,
                  config,
                },
              }
            : node
        )
      );
    }
    setShowConfigModal(false);
    setSelectedNode(null);
  };

  // ---------------------------------------------------------------------------
  // Execução isolada de node 
  // ---------------------------------------------------------------------------

  const handleRunNode = async (nodeId, config, inputData) => {
    try {
      // Find the node to get its type
      const node = nodes.find((n) => n.id === nodeId);
      if (!node) {
        throw new Error(`Node ${nodeId} not found`);
      }

      const nodeType = node.type;

      console.log("Running node:", nodeId, "Type:", nodeType, "Config:", config, "Input:", inputData);

      // Call backend endpoint to execute node in isolation
      const response = await api.post("/api/workflows/nodes/execute", {
        node_type: nodeType,
        config: config,
        input_data: inputData || {},
      });

      const result = response.data;

      // Build output data structure
      const outputData = {
        // ...(result.success !== undefined && { success: result.success }),
        timestamp: new Date().toISOString(),
        data: result.data,
        ...(result.error !== undefined && { error: result.error }),
      };

      // Update execution data state
      setNodeExecutionData((prev) => ({
        ...prev,
        [nodeId]: {
          input: inputData || {},
          output: outputData,
        },
      }));

      // Return output for modal display
      return outputData;
    } catch (error) {
      console.error("Failed to run node:", error);

      // Build error output
      const errorOutput = {
        success: false,
        timestamp: new Date().toISOString(),
        node_id: nodeId,
        error: error.response?.data?.error || error.message || "Node execution failed",
        traceback: error.response?.data?.traceback,
      };

      // Update execution data state with error
      setNodeExecutionData((prev) => ({
        ...prev,
        [nodeId]: {
          input: inputData || {},
          output: errorOutput,
        },
      }));

      throw error;
    }
  };

  // ---------------------------------------------------------------------------
  // Auto-layout com Dagre
  // ---------------------------------------------------------------------------

  const handleAutoLayout = () => {
    if (nodes.length === 0) return;

    const dagreGraph = new dagre.graphlib.Graph();
    dagreGraph.setDefaultEdgeLabel(() => ({}));
    dagreGraph.setGraph({ rankdir: "TB", nodesep: 100, ranksep: 150 });

    nodes.forEach((node) => {
      dagreGraph.setNode(node.id, { width: 180, height: 80 });
    });

    edges.forEach((edge) => {
      dagreGraph.setEdge(edge.source, edge.target);
    });

    dagre.layout(dagreGraph);

    const layoutedNodes = nodes.map((node) => {
      const nodeWithPosition = dagreGraph.node(node.id);
      return {
        ...node,
        position: {
          x: nodeWithPosition.x - 90,
          y: nodeWithPosition.y - 40,
        },
      };
    });

    setNodes(layoutedNodes);
    setTimeout(() => fitView({ padding: 0.2 }), 50);
  };

  // ---------------------------------------------------------------------------
  // Import / Export
  // ---------------------------------------------------------------------------

  const handleImport = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";

    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const imported = JSON.parse(event.target.result);

          setWorkflowName(imported.name || "Imported Workflow");
          setWorkflowDescription(imported.description || "");
          setTriggerType(imported.trigger_type || "MANUAL");

          const importedNodes = (imported.nodes || []).map((node) =>
            createFlowNode({
              ...node,
              node_type: node.node_type,
              position_x: node.position_x,
              position_y: node.position_y,
            })
          );

          const importedEdges = (imported.edges || []).map((edge) =>
            createFlowEdge({
              ...edge,
              source_node_id: edge.source_node_id,
              target_node_id: edge.target_node_id,
              condition: edge.condition,
            })
          );

          setNodes(importedNodes);
          setEdges(importedEdges);

          setTimeout(() => {
            if (importedNodes.length > 0) {
              fitView({ padding: 0.2 });
            }
          }, 50);

          alert("Workflow imported successfully!");
        } catch (error) {
          console.error(error);
          alert("Failed to import workflow: Invalid JSON file");
        }
      };
      reader.readAsText(file);
    };

    input.click();
  };

  const handleExport = () => {
    const exportData = {
      name: workflowName,
      description: workflowDescription,
      trigger_type: triggerType,
      nodes: nodes.map((node) => ({
        id: node.id,
        node_type: node.type,
        position_x: Math.round(node.position.x),
        position_y: Math.round(node.position.y),
        config: node.data.config || {},
        order: null,
      })),
      edges: edges.map((edge) => ({
        id: edge.id,
        source_node_id: edge.source,
        target_node_id: edge.target,
        condition: edge.label || null,
      })),
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `${workflowName || "workflow"}.json`;
    link.click();

    URL.revokeObjectURL(url);
  };

  // ---------------------------------------------------------------------------
  // Templates
  // ---------------------------------------------------------------------------

  const handleTemplateSelect = (templateData) => {
    setWorkflowName(templateData.name);
    setWorkflowDescription(templateData.description);
    setTriggerType(templateData.trigger_type);

    const templateNodes = (templateData.nodes || []).map((node) =>
      createFlowNode({
        ...node,
        node_type: node.node_type,
        position_x: node.position_x,
        position_y: node.position_y,
      })
    );

    const templateEdges = (templateData.edges || []).map((edge) =>
      createFlowEdge({
        ...edge,
        source_node_id: edge.source_node_id,
        target_node_id: edge.target_node_id,
        condition: edge.condition,
      })
    );

    setNodes(templateNodes);
    setEdges(templateEdges);

    setTimeout(() => {
      if (templateNodes.length > 0) {
        fitView({ padding: 0.2 });
      }
    }, 50);

    setShowTemplates(false);
  };

  // ---------------------------------------------------------------------------
  // Execução do workflow
  // ---------------------------------------------------------------------------

  const handleExecute = async () => {
    if (!workflowId) {
      alert("Please save the workflow before executing");
      return;
    }

    try {
      const execution = await executeWorkflow(workflowId, {});

      // If the execution returns logs with outputs for each node, update nodeExecutionData
      if (execution.logs && Array.isArray(execution.logs)) {
        const executionOutputs = {};

        execution.logs.forEach((log) => {
          if (log.node_id && log.output) {
            executionOutputs[log.node_id] = {
              input: log.input || {},
              output: {
                // success: true,
                data: log.output,
                timestamp: log.timestamp || new Date().toISOString(),
                // node_id: log.node_id,
                // node_type: log.node_type,
              },
            };
          }
        });

        setNodeExecutionData((prev) => ({
          ...prev,
          ...executionOutputs,
        }));
      }

      setLastExecution(execution);
      setShowExecutionViewer(true);
    } catch (error) {
      console.error(error);
      alert(`Failed to execute workflow: ${error.message}`);
    }
  };

  // ---------------------------------------------------------------------------
  // Validação e salvar
  // ---------------------------------------------------------------------------

  const handleValidate = () => {
    const validation = validateWorkflow(nodes, edges);
    setValidationErrors(validation.errors);

    if (!validation.isValid) {
      alert(`Workflow validation failed:\n${validation.errors.join("\n")}`);
    }

    return validation.isValid;
  };

  const handleSave = async () => {
    if (!workflowName.trim()) {
      alert("Please enter a workflow name");
      return;
    }

    if (nodes.length === 0) {
      alert("Please add at least one node to the workflow");
      return;
    }

    // if (!handleValidate()) return;

    setSaving(true);

    try {
      const backendNodes = nodes.map((node) => ({
        id: node.id,
        node_type: node.type,
        position_x: Math.round(node.position.x),
        position_y: Math.round(node.position.y),
        config: node.data.config || {},
        order: null,
      }));

      const backendEdges = edges.map((edge) => ({
        id: edge.id,
        source_node_id: edge.source,
        target_node_id: edge.target,
        condition: edge.label || null,
      }));

      const workflowData = {
        name: workflowName,
        description: workflowDescription,
        trigger_type: triggerType,
        trigger_config: {},
        is_active: false,
        nodes: backendNodes,
        edges: backendEdges,
      };

      if (workflowId) {
        await updateWorkflow(workflowId, workflowData);
        alert("Workflow updated successfully!");
      } else {
        await createWorkflow(workflowData);
        alert("Workflow created successfully!");
      }

      navigate("/smartflow");
    } catch (error) {
      console.error(error);
      alert(`Failed to save workflow: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (
      window.confirm(
        "Are you sure you want to go back? Unsaved changes will be lost."
      )
    ) {
      navigate("/smartflow");
    }
  };

  // ---------------------------------------------------------------------------
  // UI
  // ---------------------------------------------------------------------------

  return (
    <div
      style={{
        display: "flex",
        height: "calc(100vh - 60px)",
        background: "#f5f6fa",
      }}
    >
      {/* Paleta de Nós */}
      <NodePalette />

      {/* Área principal */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        {/* Toolbar */}
        <div
          style={{
            background: "white",
            padding: "15px 20px",
            borderBottom: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "15px",
              alignItems: "center",
              flex: 1,
            }}
          >
            <input
              type="text"
              value={workflowName}
              onChange={(e) => setWorkflowName(e.target.value)}
              placeholder="Workflow Name"
              style={{
                padding: "8px 12px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                fontSize: "14px",
                width: "250px",
              }}
            />
            <input
              type="text"
              value={workflowDescription}
              onChange={(e) => setWorkflowDescription(e.target.value)}
              placeholder="Description (optional)"
              style={{
                padding: "8px 12px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                fontSize: "14px",
                width: "300px",
              }}
            />
            <select
              value={triggerType}
              onChange={(e) => setTriggerType(e.target.value)}
              style={{
                padding: "8px 12px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            >
              <option value="MANUAL">Manual</option>
              <option value="CRM_LEAD_CREATED">Lead Created</option>
              <option value="CRM_STAGE_CHANGED">Stage Changed</option>
              <option value="SCHEDULED">Scheduled</option>
              <option value="WEBHOOK">Webhook</option>
            </select>
          </div>

          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={() => setShowTemplates(true)}
              style={{
                padding: "8px 14px",
                background: "#9b59b6",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "13px",
              }}
              title="Use Template"
            >
              Templates
            </button>
            <button
              onClick={handleAutoLayout}
              disabled={nodes.length === 0}
              style={{
                padding: "8px 14px",
                background: nodes.length === 0 ? "#bdc3c7" : "#16a085",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: nodes.length === 0 ? "not-allowed" : "pointer",
                fontSize: "13px",
              }}
              title="Auto-arrange nodes"
            >
              Auto-Layout
            </button>
            <button
              onClick={handleImport}
              style={{
                padding: "8px 14px",
                background: "#27ae60",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "13px",
              }}
              title="Import workflow from JSON"
            >
              Import
            </button>
            <button
              onClick={handleExport}
              disabled={nodes.length === 0}
              style={{
                padding: "8px 14px",
                background: nodes.length === 0 ? "#bdc3c7" : "#f39c12",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: nodes.length === 0 ? "not-allowed" : "pointer",
                fontSize: "13px",
              }}
              title="Export workflow to JSON"
            >
              Export
            </button>
            <button
              onClick={handleValidate}
              disabled={nodes.length === 0}
              style={{
                padding: "8px 14px",
                background: nodes.length === 0 ? "#bdc3c7" : "#e67e22",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: nodes.length === 0 ? "not-allowed" : "pointer",
                fontSize: "13px",
              }}
              title="Validate workflow"
            >
              Validate
            </button>
            {workflowId && (
              <button
                onClick={handleExecute}
                style={{
                  padding: "8px 14px",
                  background: "#2ecc71",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "13px",
                }}
                title="Execute workflow"
              >
                Execute
              </button>
            )}
            <button
              onClick={handleBack}
              style={{
                padding: "8px 14px",
                background: "#95a5a6",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "13px",
              }}
            >
              Back
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: "8px 16px",
                background: saving ? "#95a5a6" : "#3498db",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: saving ? "not-allowed" : "pointer",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <div
            style={{
              background: "#fff3cd",
              padding: "10px 20px",
              borderBottom: "1px solid #ffc107",
              color: "#856404",
              fontSize: "13px",
            }}
          >
            <strong>Validation Errors:</strong>
            <ul style={{ margin: "5px 0", paddingLeft: "20px" }}>
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Canvas ReactFlow */}
        <div style={{ flex: 1, position: "relative" }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onNodeDoubleClick={onNodeDoubleClick}
            onNodesDelete={onNodesDelete}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.2 }}
          >
            <Controls />
            <MiniMap />
            <Background variant="dots" gap={12} size={1} />
          </ReactFlow>

          {nodes.length === 0 && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
              }}
            >
              <div
                style={{
                  background: "rgba(255,255,255,0.9)",
                  padding: "16px 20px",
                  borderRadius: "8px",
                  border: "1px dashed #ccc",
                  fontSize: "14px",
                  color: "#555",
                  textAlign: "center",
                  maxWidth: "320px",
                }}
              >
                Arraste um <strong>gatilho</strong> ou <strong>ação</strong> do
                painel à esquerda para começar a montar sua automação.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Config Modal */}
      {showConfigModal && selectedNode && (
        <NodeConfigModal
          key={`config-modal-${selectedNode.id}`}
          node={selectedNode}
          inputData={getInputDataForNode(selectedNode.id)}
          outputData={nodeExecutionData[selectedNode.id]?.output}
          onSave={handleConfigSave}
          onClose={() => {
            setShowConfigModal(false);
            setSelectedNode(null);
          }}
          onRunNode={handleRunNode}
        />
      )}

      {/* Templates Modal */}
      {showTemplates && (
        <WorkflowTemplates
          onSelectTemplate={handleTemplateSelect}
          onClose={() => setShowTemplates(false)}
        />
      )}

      {/* Execution Viewer */}
      {showExecutionViewer && lastExecution && (
        <ExecutionViewer
          execution={lastExecution}
          workflow={currentWorkflow}
          onClose={() => setShowExecutionViewer(false)}
        />
      )}
    </div>
  );
};

// -----------------------------------------------------------------------------
// Wrapper com ReactFlowProvider (obrigatório para useReactFlow e DnD funcionar)
// -----------------------------------------------------------------------------
const WorkflowBuilder = () => {
  return (
    <ReactFlowProvider>
      <WorkflowBuilderInner />
    </ReactFlowProvider>
  );
};

export default WorkflowBuilder;
